/* jc-logo-slider.js — JC-EVO 09/26/2026, logo slider design A (loaded as a module by sections/logo-slider.liquid).
   Even optical logo sizing. The press PNGs ship on their own canvases (most 240x160 with 55-80% empty space above and
   below the mark, HGTV a tight block), so no plain CSS box makes them look the same size. For each logo file this reads
   the opaque bounds once from a small CORS copy (Shopify's CDN sends Access-Control-Allow-Origin: *), then adds one CSS
   rule per file that sets the image width and trims the empty margins (negative margins), in units of --logo-h:
   visible height = --logo-h * (4 / aspect)^0.46, clamped to 0.7-1.7, so long wordmarks get shorter and compact marks
   taller at a similar visual weight. The rule matches by src, so marquee.js clones made at any time are sized too.
   A logo that cannot be measured keeps the plain fallback box from jc-logo-slider.css. Store images are never changed. */
const sheet = document.createElement('style');
sheet.id = 'jc-logo-slider-sizes';
const done = new Set();
// JC-EVO 09/30/2026 N01 (core fork): the approved press logos' numbers, precompiled with exactly the measurement below (same
// 120 px copy, same rounding; harvested from this theme's own rules in Chrome and WebKit). A known logo gets its rule without the
// measurement download, canvas scan or per-image stylesheet rewrite; all known rules go in ONE append after the marquees showing them are built. Key = file + ?v=, so a file
// replaced in the editor (new v) or a src with any other parameter than v/width falls back to the live measurement below.
// Values: [width, margin-top, margin-right, margin-bottom, margin-left] in --logo-h, exactly as the measured rule prints them.
const KNOWN = {
  "files/RKIN_Kickstarter_1.webp?v=1790538952": ["5.8159", "-1.5509", "0.0000", "-1.5994", "0.0000"],
  "files/RKIN_as_Seen_on_GadgetFlow_1.webp?v=1790538971": ["5.1189", "-1.3224", "0.0000", "-1.2797", "0.0000"],
  "files/RKIN_as_Seen_on_Tap_Score_1.webp?v=1790538970": ["4.7293", "-1.1429", "0.0000", "-1.1429", "0.0000"],
  "files/RKIN_as_seen_in_HGTV_Magazine-webp.webp?v=1790538970": ["2.2940", "0.0000", "0.0000", "0.0000", "0.0000"],
  "files/RKIN_as_seen_in_Rolling_Stone_Magazine.webp?v=1790538971": ["4.2342", "-0.7763", "0.0000", "-0.8116", "0.0000"],
  "files/RKIN_as_seen_on_The_Spruce_Eats-webp.webp?v=1790538970": ["3.5717", "-0.6846", "0.0000", "-0.7143", "0.0000"]
};
// Only this site's own /cdn/shop/files/<name> URLs with exactly one v (+ at most one width) are keyed (Codex N01 review #2).
const knownKey = (src) => {
  try {
    const u = new URL(src, location.href), q = [...u.searchParams.keys()];
    if (u.origin !== location.origin || !/^\/cdn\/shop\/files\/[^/]+$/.test(u.pathname)) return null;
    if (q.filter((k) => k === 'v').length !== 1 || q.filter((k) => k === 'width').length > 1 || q.some((k) => k !== 'v' && k !== 'width') || !u.searchParams.get('v')) return null;
    return u.pathname.slice('/cdn/shop/'.length) + '?v=' + u.searchParams.get('v');
  } catch (e) { return null; }
};
const knownRule = (src, g) => `.logo-slider .logo-slider__item--logo img[src="${CSS.escape(src)}"]{width:calc(var(--logo-h) * ${g[0]})!important;height:auto!important;max-width:none!important;max-height:none!important;margin:calc(var(--logo-h) * ${g[1]}) calc(var(--logo-h) * ${g[2]}) calc(var(--logo-h) * ${g[3]}) calc(var(--logo-h) * ${g[4]})!important}\n`;

function bounds(src) {
  return new Promise((resolve) => {
    const url = new URL(src, location.href);
    url.searchParams.set('width', '120'); // small copy for measuring; margins scale with the image
    url.searchParams.set('jcn', '1'); // never share a cache entry with the page's own (non-CORS) request
    const im = new Image();
    const timer = setTimeout(() => resolve(null), 8000);
    im.crossOrigin = 'anonymous';
    im.onerror = () => { clearTimeout(timer); resolve(null); };
    im.onload = () => {
      clearTimeout(timer);
      try {
        const w = im.naturalWidth, h = im.naturalHeight;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(im, 0, 0);
        const d = ctx.getImageData(0, 0, w, h).data;
        let t = h, b = -1, l = w, r = -1;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          if (d[(y * w + x) * 4 + 3] > 16) { if (y < t) t = y; if (y > b) b = y; if (x < l) l = x; if (x > r) r = x; }
        }
        resolve(b < 0 ? null : { w, h, t, b: b + 1, l, r: r + 1 });
      } catch (e) { resolve(null); }
    };
    im.src = url.href;
  });
}

async function size(src) {
  if (!src || done.has(src)) return;
  done.add(src);
  const m = await bounds(src);
  if (!m) return;
  const cw = m.r - m.l, ch = m.b - m.t, ratio = cw / ch;
  const f = Math.min(1.7, Math.max(0.7, Math.pow(4 / ratio, 0.46))); // visible height, in --logo-h
  const kw = f * ratio * (m.w / cw); // whole image width, in --logo-h
  const u = kw / m.w; // --logo-h per source pixel
  const k = (px) => `calc(var(--logo-h) * ${(-px * u).toFixed(4)})`;
  if (!sheet.isConnected) document.head.appendChild(sheet);
  sheet.textContent += `.logo-slider .logo-slider__item--logo img[src="${CSS.escape(src)}"]{width:calc(var(--logo-h) * ${kw.toFixed(4)})!important;height:auto!important;max-width:none!important;max-height:none!important;margin:${k(m.t)} ${k(m.w - m.r)} ${k(m.h - m.b)} ${k(m.l)}!important}\n`;
}

// marquee.js copies (the repeated item lists and the aria-hidden copy of the content) only feed the phone drift: keep
// them out of the Tab and screen-reader order (one stop per logo once a logo has a link), whenever marquee.js makes them
const quiet = (w) => w.querySelectorAll('.logo-slider__content[aria-hidden], .logo-slider__content > .logo-slider__items:not(:first-child)')
  .forEach((el) => { if (!el.inert) { el.setAttribute('aria-hidden', 'true'); el.inert = true; } });

// N01: the known rules go in ONE append, written right AFTER marquee.js has built its copies (the aria-hidden content clone) in
// EVERY marquee that shows one of them, so each marquee still sizes its copies from the same pre-measurement layout as the measured
// path (Codex N01 reviews: motion parity; shared sources across marquees). The pending rules, their sources and the marquees to wait
// for are module-wide, so a section loaded while rules are still pending (theme editor) is held too (Codex wave A post-review). One 8 s
// deadline per batch, from its first pending rule, mirrors the measurement timeout (after it the rules are written even if a marquee has
// not built its copies); logos outside a marquee do not wait.
let pendingRules = '', pendingTimer = 0, pendingObserver = null;
const pendingSources = new Set(), gates = new Set();
const gatesReady = () => [...gates].every((w) => !w.isConnected || !!w.querySelector(':scope > .logo-slider__content[aria-hidden]'));
const writePending = () => {
  pendingObserver?.disconnect(); pendingObserver = null; clearTimeout(pendingTimer); pendingTimer = 0;
  const rules = pendingRules; pendingRules = ''; pendingSources.clear(); gates.clear();
  if (!rules) return;
  if (!sheet.isConnected) document.head.appendChild(sheet);
  sheet.textContent += rules;
};
const run = (root) => {
  root.querySelectorAll('.logo-slider__item--logo img[src]').forEach((img) => {
    const src = img.getAttribute('src');
    const key = src && !done.has(src) && knownKey(src), g = key && Object.prototype.hasOwnProperty.call(KNOWN, key) && KNOWN[key];
    if (!g) { size(src); return; }
    done.add(src);
    pendingSources.add(src);
    pendingRules += knownRule(src, g);
  });
  if (pendingRules) {
    pendingObserver = pendingObserver || new MutationObserver(() => { if (gatesReady()) writePending(); });
    root.querySelectorAll('marquee-component .logo-slider__wrapper').forEach((w) => {
      if (!gates.has(w) && [...pendingSources].some((s) => w.querySelector(`img[src="${CSS.escape(s)}"]`))) { gates.add(w); pendingObserver.observe(w, { childList: true }); }
    });
    if (!pendingTimer) pendingTimer = setTimeout(writePending, 8000);
    if (gatesReady()) writePending();
  }
  root.querySelectorAll('.logo-slider--logos .logo-slider__wrapper').forEach((w) => {
    quiet(w);
    new MutationObserver(() => quiet(w)).observe(w, { childList: true, subtree: true });
  });
};
run(document);
document.addEventListener('shopify:section:load', (e) => run(e.target)); // theme editor re-renders a section
// Eric 09/27/2026 "Water, explained." Option 1: previous / next paddles for the series row (jc-logo-slider.css makes it one
// scrolling row). They page by the visible width, grey out at either end and hide when everything fits. Without this
// script the row still scrolls by swipe, trackpad and keyboard focus.
const chevron = (d) => `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const gallery = (root) => root.querySelectorAll('.logo-slider--series').forEach((series) => {
  const row = series.querySelector('.logo-slider__items');
  if (!row || series.querySelector('.jc-series-nav')) return;
  const nav = document.createElement('div');
  nav.className = 'jc-series-nav';
  nav.innerHTML = `<button type="button" class="jc-series-nav__btn" data-dir="-1" aria-label="Previous videos">${chevron('M15 5l-7 7 7 7')}</button>`
    + `<button type="button" class="jc-series-nav__btn" data-dir="1" aria-label="Next videos">${chevron('M9 5l7 7-7 7')}</button>`;
  row.after(nav);
  const [prev, next] = nav.querySelectorAll('button');
  const update = () => {
    const max = row.scrollWidth - row.clientWidth;
    const fits = max <= 1, atStart = row.scrollLeft <= 1, atEnd = row.scrollLeft >= max - 1;
    const f = document.activeElement; // a paddle that gets disabled or hidden would drop keyboard focus: move it first
    if (fits && nav.contains(f)) row.querySelector('.jcv-poster')?.focus({ preventScroll: true });
    else if (atEnd && f === next) prev.focus();
    else if (atStart && f === prev) next.focus();
    nav.hidden = fits;
    prev.disabled = atStart;
    next.disabled = atEnd;
  };
  nav.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (b) row.scrollBy({ left: Number(b.dataset.dir) * row.clientWidth });
  });
  row.addEventListener('scroll', update, { passive: true });
  const watch = new ResizeObserver(update);
  watch.observe(row);
  series.jcGalleryStop = () => watch.disconnect();
  update();
});
gallery(document);
document.addEventListener('shopify:section:load', (e) => gallery(e.target));
document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('.logo-slider--series').forEach((s) => s.jcGalleryStop?.()));

// JC-EVO 09/30/2026 a11y (WCAG 2.2.2 Pause, Stop, Hide, audit T6): phones drift the logo row without end. The Pause / Play button
// rendered by sections/logo-slider.liquid (shown by jc-logo-slider.css below 750 px when motion is allowed) sets data-jc-paused on
// the marquee, which pauses the CSS animation. Independent of the N01 sizing code above.
const pauseToggles = (root) => root.querySelectorAll('.logo-slider--logos').forEach((s) => {
  const b = s.querySelector(':scope > .jc-marquee-toggle');
  const m = s.querySelector('marquee-component.logo-slider__marquee');
  if (!b || !m || b.dataset.jcBound) return;
  b.dataset.jcBound = '1';
  const set = (paused) => {
    m.toggleAttribute('data-jc-paused', paused);
    b.toggleAttribute('data-paused', paused);
    b.setAttribute('aria-label', paused ? b.dataset.labelPlay : b.dataset.labelPause);
  };
  b.addEventListener('click', () => set(!m.hasAttribute('data-jc-paused')));
  set(false);
  b.hidden = false;
});
pauseToggles(document);
document.addEventListener('shopify:section:load', (e) => pauseToggles(e.target));

/* JC-EVO 10/07/2026 (RC10 a11y, Eric): observe original items in native-scrolling series rows. */
function jcVisibleCards(root, selector) {
  if (!root || !window.IntersectionObserver || !window.MutationObserver) return null;
  var observed = [];
  // JC-EVO 10/07/2026 (RC10 a11y, Eric): retain focus; reconcile once after resize.
  var resizeTimer = 0, focusTimer = 0, sizeObserver = null;
  function setVisible(card, visible) {
    if (visible || (document.activeElement && card.contains(document.activeElement))) {
      if (card.hasAttribute('aria-hidden')) card.removeAttribute('aria-hidden');
      if (card.hasAttribute('inert')) card.removeAttribute('inert');
    } else {
      if (card.getAttribute('aria-hidden') !== 'true') card.setAttribute('aria-hidden', 'true');
      if (!card.hasAttribute('inert')) card.setAttribute('inert', '');
    }
  }
  function sync() {
    if (!root.isConnected) { stop(); return; }
    var box = root.getBoundingClientRect();
    var left = box.left + root.clientLeft, top = box.top + root.clientTop;
    var right = left + root.clientWidth, bottom = top + root.clientHeight;
    var cards = root.querySelectorAll(selector);
    for (var i = 0; i < observed.length; i++) {
      if (!root.contains(observed[i])) { observer.unobserve(observed[i]); observed.splice(i--, 1); }
    }
    for (var j = 0; j < cards.length; j++) {
      var card = cards[j];
      // Marquee copies stay hidden; only the originals belong to this observer.
      if (card.closest('.logo-slider__content[aria-hidden], .logo-slider__items[aria-hidden]')) continue;
      if (observed.indexOf(card) === -1) { observed.push(card); observer.observe(card); }
      var rect = card.getBoundingClientRect();
      var visible = Math.min(rect.right, right) > Math.max(rect.left, left) && Math.min(rect.bottom, bottom) > Math.max(rect.top, top);
      setVisible(card, visible);
    }
  }
  var observer = new IntersectionObserver(function (entries) {
    if (!root.isConnected) { stop(); return; }
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i], card = entry.target;
      if (!root.contains(card) || card.closest('.logo-slider__content[aria-hidden], .logo-slider__items[aria-hidden]')) continue;
      setVisible(card, entry.isIntersecting && entry.intersectionRect.width > 0);
    }
  }, { root: root, threshold: 0 });
  var changes = new MutationObserver(sync);
  changes.observe(root, { childList: true, subtree: true });
  root.addEventListener('scroll', sync, { passive: true });
  function resized() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () { resizeTimer = 0; sync(); }, 100);
  }
  function focusLeft() {
    window.clearTimeout(focusTimer);
    focusTimer = window.setTimeout(function () { focusTimer = 0; sync(); }, 0);
  }
  root.addEventListener('focusout', focusLeft);
  window.addEventListener('resize', resized, { passive: true });
  if (window.ResizeObserver) { sizeObserver = new window.ResizeObserver(resized); sizeObserver.observe(root); }
  function stop() {
    observer.disconnect(); changes.disconnect();
    root.removeEventListener('scroll', sync);
    root.removeEventListener('focusout', focusLeft);
    window.removeEventListener('resize', resized);
    window.clearTimeout(resizeTimer); window.clearTimeout(focusTimer);
    if (sizeObserver) sizeObserver.disconnect();
  }
  sync();
  return { sync: sync, stop: stop };
}
function jcLogoVisibility(root) {
  if (!root || !window.IntersectionObserver) return;
  root.querySelectorAll('.logo-slider').forEach(function (section) {
    if (section.jcVisibility) return;
    // JC-EVO 10/07/2026 (RC10 a11y, Eric): skip compositor-driven marquees; IO misses motion and frame polling costs too much.
    if (!section.classList.contains('logo-slider--series')) return;
    var viewport = section.querySelector('.logo-slider__items');
    if (!viewport) return;
    section.jcVisibility = jcVisibleCards(viewport, '.logo-slider__items > [class*="logo-slider__item-"]');
  });
}
jcLogoVisibility(document);
document.addEventListener('shopify:section:load', function (e) { jcLogoVisibility(e.target); });
document.addEventListener('shopify:section:unload', function (e) {
  e.target.querySelectorAll('.logo-slider').forEach(function (section) {
    if (section.jcVisibility) { section.jcVisibility.stop(); section.jcVisibility = null; }
  });
});
